import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** Overflow for table-only controls (fold, columns, export), so the toolbar keeps to search, filters and sort. */
export function OpportunityHubTableMenu({
  foldControl,
  columnsControl,
  onExportCsv,
  exportDisabled = false,
}: {
  /** Omitted when there is nothing to fold, e.g. the bare no-persona list. */
  foldControl?: ReactNode;
  columnsControl: ReactNode;
  onExportCsv: () => void;
  exportDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const menuLabel = t("personaOpportunities.hub.opportunities.tableMenu.label", "Table options");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="more-horizontal"
          aria-label={menuLabel}
          className="px-2"
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" aria-label={menuLabel}>
        {foldControl ? (
          <MenuSection
            title={t("personaOpportunities.hub.opportunities.tableMenu.groups", "Groups")}
            action={foldControl}
          />
        ) : null}
        <MenuSection
          title={t("personaOpportunities.hub.columns.manage", "Manage columns")}
        >
          {columnsControl}
        </MenuSection>
        <div className="mt-1 border-t border-border pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="download"
            onClick={onExportCsv}
            disabled={exportDisabled}
            className="w-full justify-start"
          >
            {t("personaOpportunities.hub.opportunities.exportCsv", "Export CSV")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MenuSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="pb-1">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}
